import { useState, useCallback, useRef } from 'react';

// ─── Set serialization ───────────────────────────────────────────────────────
// JSON doesn't handle Sets natively. We encode them as { $set: [...values] }
// so the hook transparently round-trips Set values to/from localStorage.

function replacer(_key: string, value: unknown): unknown {
  if (value instanceof Set) return { $set: [...value] };
  return value;
}

function reviver(_key: string, value: unknown): unknown {
  if (
    value !== null &&
    typeof value === 'object' &&
    '$set' in (value as object)
  ) {
    return new Set((value as { $set: unknown[] }).$set);
  }
  return value;
}

// ─── Storage helpers ─────────────────────────────────────────────────────────

function readStorage<T extends object>(storageKey: string, defaults: T): T {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw, reviver) as Partial<T>;
    // Only carry over keys present in defaults — ignores stale or removed keys.
    const merged = { ...defaults };
    for (const key of Object.keys(defaults) as (keyof T)[]) {
      if (key in parsed) (merged as T)[key] = (parsed as T)[key];
    }
    return merged;
  } catch {
    return defaults;
  }
}

function writeStorage<T>(storageKey: string, value: T): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(value, replacer));
  } catch {
    // Private browsing or storage quota exceeded — fail silently.
  }
}

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Persistent, route-scoped UI preferences backed by localStorage.
 *
 * Works like useState but automatically reads/writes localStorage.
 * Set values are transparently serialized so callers can use Set<T> directly.
 *
 * The `storageKey` should be route-scoped (e.g. `${pathname}:filters`) so
 * preferences for `/decks` and `/my-decks` are stored independently.
 *
 * @example
 *   const { pathname } = useLocation();
 *   const [prefs, setPrefs] = useRoutePrefs(`${pathname}:grid`, {
 *     groupMode: 'recent' as GroupMode,
 *     sortDir: null as SortDir,
 *   });
 *   // setPrefs({ groupMode: 'bracket' }) — merges, saves, re-renders
 *   // setPrefs((stored) => ({ ... })) — an updater over the stored value (several components sharing a key)
 */
export function useRoutePrefs<T extends object>(
  storageKey: string,
  defaults: T,
): [T, (patch: Partial<T> | ((stored: T) => Partial<T>)) => void] {
  const lsKey = `ui:${storageKey}`;

  const [prefs, setPrefsState] = useState<T>(() => readStorage(lsKey, defaults));
  // The last rendered value: what an updater starts from when storage holds nothing or cannot be read.
  const latest = useRef(prefs);
  latest.current = prefs;

  const setPrefs = useCallback(
    (patch: Partial<T> | ((stored: T) => Partial<T>)) => {
      // An updater reads the stored value: several components on one key (one per theme section) then never
      // overwrite each other's writes with their own render-time copy. It runs here, once, and hands React a value:
      // a state updater that touches storage is replayed under StrictMode and would undo its own write.
      if (typeof patch === 'function') {
        const stored = readStorage(lsKey, latest.current);
        const next = { ...stored, ...patch(stored) };
        writeStorage(lsKey, next);
        setPrefsState(next);
        return;
      }
      setPrefsState(prev => {
        const next = { ...prev, ...patch };
        writeStorage(lsKey, next);
        return next;
      });
    },
    [lsKey],
  );

  return [prefs, setPrefs];
}
